/**
 * Section 10a — CLH Band.
 *
 * The maths lives in utils/clhBand.ts (with a chartPort twin checked by
 * execution in bigmove/parity.ts). Every number on this page is recomputed by
 * backend/src/scripts/clhband/verifyClhBand.ts.
 */
import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_indicator_clhband: GuideSection = {
  id: 'indicator-clhband',
  label: 'CLH Band',
  group: 'reference',
  icon: <StackedLineChartIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>CLH Band</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        CLH candles are smoothed, so their levels are not real prices. This turns them back into one:
        <strong> the actual price the next candle would have to close at for the next CLH body to break out of
        this one.</strong> Drawn on any candle type, optionally for the higher timeframe too.
      </Typography>

      <GuideCallout kind="info" title="What the two lines mean">
        <strong>Upper</strong> — the price at which the next CLH body would close above this body&rsquo;s top.
        <strong> Lower</strong> — the price at which it would close below this body&rsquo;s bottom. Between them,
        the next body lands inside the current one: no decision either way.
      </GuideCallout>

      <H id="clh-roles">The lines switch roles — read them accordingly</H>
      <P>
        The upper line sits <strong>below</strong> the current price on about half of all bars. That is not a
        fault. When CLH is already rising, the next body clears the old body top even if price does not move at
        all — so on that side the line stops being a breakout trigger and becomes <em>the level at which the
        up-move would fail</em>, a trailing support. The lower line behaves the same way in reverse during a
        downtrend.
      </P>
      <P>
        So: <strong>a line on the far side of price is a trigger; a line on your side of price is a stop.</strong>{' '}
        The optional third line — the flip level, drawn in the middle — is the one that is always a genuine
        trend change, because it marks where the next body changes <em>colour</em> rather than where it clears an
        old one.
      </P>

      <H id="clh-midcolour">The middle line is coloured by its own slope</H>
      <P>
        <strong>Falling is green, rising is red</strong> — which is the right way round once you see what the line
        is. It is the price the next candle has to close at for the CLH body to turn green. When that level is
        <em> coming down</em>, the bar you need is getting easier to print: the market is walking towards a flip in
        your favour. When it is <em>climbing away</em>, the flip is getting more expensive with every bar.
      </P>
      <P>
        A flat step is not a change of direction, so it keeps the colour of the move that got it there, and the
        first few bars take the first direction the line actually goes rather than opening in a colour they have
        not earned. The current timeframe and the higher timeframe are read the same way, and both colours are
        yours to change.
      </P>
      <P>
        The higher-timeframe midline can also <strong>paint the chart background</strong>: Chart Settings &rarr;
        Indicators &rarr; Bkground, set <em>Compute by</em> to <strong>CLH midline (HTF)</strong>. Green background
        where that line is falling, red where it is rising. The CLH Band itself does not have to be switched on.
      </P>

      <H id="clh-far">Why the levels are sometimes a long way off</H>
      <P>
        CLH is heavily smoothed — an EMA of an EMA of Heikin-Ashi, averaged with an EMA of a 14-bar regression.
        Measured on the shipped code, <strong>the CLH close moves only 2.79% of whatever the price moves</strong>.
        So when CLH is trending hard it genuinely takes a very large move to break it, and about a quarter of
        bars put the level beyond ten ATR.
      </P>
      <P>
        The band is drawn so this costs you nothing: it never drives the price axis, so a distant level simply
        clips off the top or bottom instead of flattening your candles. The price-scale labels stay visible, so
        you can still read where the level is even when the line itself is off-screen.
      </P>

      <H id="clh-nofuture">It cannot look into the future</H>
      <P>
        You asked for levels that are reliable, so this is enforced in three separate places and proven rather
        than asserted:
      </P>
      <Box component="ul" sx={{ pl: 3, mb: 2 }}>
        <li><Typography variant="body2" sx={{ mb: 1 }}>
          Every input comes from the current candle and earlier. The next body&rsquo;s <em>open</em> is already
          fixed once this candle closes, which is what makes the level solvable at all.
        </Typography></li>
        <li><Typography variant="body2" sx={{ mb: 1 }}>
          The level shown against a candle governs the candle <strong>after</strong> it, and is drawn one bar to
          the right. It becomes known only at that candle&rsquo;s close.
        </Typography></li>
        <li><Typography variant="body2">
          The higher-timeframe lines use the last HTF candle that has fully <strong>closed</strong> — never the
          one currently forming, whose close lies in the future of every bar inside it.
        </Typography></li>
      </Box>
      <P>
        The check that proves it is prefix-invariance: recompute the band using only the history up to some
        candle, and the level there must equal the one computed from the full series. Anything peeking ahead
        would differ. It matches to within 1.8e-11 of price.
      </P>

      <H id="clh-exact">How exact is it?</H>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow><TableCell>Check</TableCell><TableCell align="right">Result</TableCell></TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell>Solved level fed back through the real CLH transform lands on its target</TableCell><TableCell align="right">within 8.3e-13 of price</TableCell></TableRow>
          <TableRow><TableCell>The solve is a true closed form, not an approximation</TableCell><TableCell align="right">7.5e-16</TableCell></TableRow>
          <TableRow><TableCell>No look-ahead (prefix-invariance)</TableCell><TableCell align="right">1.8e-11</TableCell></TableRow>
          <TableRow><TableCell>Bars that carry a level</TableCell><TableCell align="right">96–99.8%</TableCell></TableRow>
        </TableBody>
      </Table>
      <P>
        The level is exact because every step of the CLH transform is linear and none of its min/max calls touch
        the close — they only shape the drawn high and low. The solver does not re-implement any of that: it
        measures the transform&rsquo;s own response at runtime and inverts it, so if the candle maths ever
        changes, the band follows it automatically.
      </P>

      <H id="clh-htf">The higher-timeframe lines</H>
      <P>
        Switch on the HTF half and you get the same two levels computed from the mapped higher timeframe (the
        same ladder the Strategies page uses — a 4-hour chart is mapped to the daily, a daily chart to the
        weekly), drawn dimmer behind the main pair. Its width and line style are set separately from this
        timeframe&rsquo;s, so the HTF midline can be read on its own without thinning or dashing the lines you are
        actually trading.
      </P>
      <P>
        Because they come from the last <em>closed</em> higher-timeframe candle, they are fixed and known in
        advance for the whole of the candle currently forming. That is the point: you can act on a
        lower-timeframe close while already knowing where the higher timeframe flips, instead of waiting for a
        large HTF candle to finish with nothing left in it.
      </P>

      <H id="clh-not">What it is not</H>
      <P>
        <strong>Not BO_D / BO_W.</strong> Those are also CLH breakout levels but a completely different
        quantity — the highest high and lowest low of the last N completed CLH candles. This one inverts the
        candle maths instead.
      </P>
      <P>
        <strong>Not an order.</strong> Nothing here places, sizes or arms anything. If these lines are later
        wired to BBUY / BSHORT triggers, one thing has to be decided first: these levels are a <em>close</em>{' '}
        condition, while a stop order fires on <em>touch</em>. Price can touch a line and close back inside, so
        a touch-triggered order would fire on candles the CLH body never actually broke.
      </P>
      <OpenInApp to="/charts" label="Open Charts" />
    </>
  ),
};

export default Section_indicator_clhband;
